import React, { FC } from 'react';
import { Customer, CustomerAttributes, JimboResponse } from '@models';
import { LoadingButton } from '@mui/lab';
import { Dialog, DialogContent, Button, Grid, DialogTitle, IconButton, Typography, DialogActions } from '@mui/material';
import { useDeleteCustomerAttributeMutation, useResetPasswordMutation } from '@services';
import { setSelectedCustomer } from '@store/slices/customer';
import { showToast } from '@store/slices/notification';
import { addErrorToLog } from '@store/slices/error-log';
import { useAppDispatch } from '@hooks/redux-custom';
import CloseIcon from '@mui/icons-material/Close';

type Props = {
  customer: Customer;
  isOpen: boolean;
  onClose?: (isCancelled?: boolean) => void;
  bigCCustomerAttributes?: CustomerAttributes[] | undefined;
  modalContent: {
    attributesToDelete: string[];
    header: string;
    body: string;
  };
  resetBtnLabel: string;
};

export const ResetModal: FC<Props> = ({
  customer,
  isOpen,
  onClose = (isCancelled = false) => false,
  bigCCustomerAttributes,
  modalContent,
  resetBtnLabel = 'Reset',
}) => {
  const dispatch = useAppDispatch();

  const [deleteAttribute] = useDeleteCustomerAttributeMutation();
  const [resetPassword] = useResetPasswordMutation();

  const phoneNumberReset = modalContent.attributesToDelete[0] === 'phonenumberverifieddate';
  const passwordReset = modalContent.attributesToDelete[0] === 'password';

  // isResetVerified set to true if any attribute name starts with id
  const resetAttribute = async (attributeID: number, attributeName: string | undefined) => {
    if (attributeID) {
      deleteAttribute({
        attribute_id: attributeID,
        customer_id: customer.id,
        isResetIdVerified: attributeName?.search(/^id\w/i) !== -1,
        isDateOfBirthReset: attributeName === 'dob',
      })
        .unwrap()
        .then(() => {
          dispatch(
            showToast({
              message: `Successfully reset attribute`,
              type: 'success',
            }),
          );
        })
        .catch((error: { data: JimboResponse }) => {
          dispatch(addErrorToLog(error?.data));

          dispatch(
            showToast({
              message: `Failed to reset attribute: ${attributeName}`,
              type: 'error',
            }),
          );
        });
    }
  };

  const handlePasswordReset = () => {
    customer &&
      resetPassword(customer)
        .unwrap()
        .then((data) =>
          dispatch(
            showToast({
              message: data.message || 'Reset password email sent to the user.',
              type: 'success',
            }),
          ),
        )
        .catch((error: { data: JimboResponse }) => {
          dispatch(addErrorToLog(error?.data));

          dispatch(
            showToast({
              message: error.data?.message || 'Failed to send password reset email to the user.',
              type: 'error',
            }),
          );
        });
  };

  const resetAttributes = async () => {
    const attributeDeleteIds = modalContent.attributesToDelete.map((attributeName: string) => {
      const attributeID = bigCCustomerAttributes?.find((attr: CustomerAttributes) => attr.name === attributeName)?.id;

      const idToDelete = customer.attributes.find((attribute) => attribute.attribute_id === attributeID)?.id;

      resetAttribute(idToDelete!, attributeName); // donot clear the phone number on reset

      return idToDelete;
    });

    const updatedAttributes = customer.attributes.map((attribute) => {
      if (attributeDeleteIds.includes(attribute.id)) {
        return { ...attribute, attribute_value: '' };
      }

      return attribute;
    });

    const updatedCustomer = { ...customer, phone: phoneNumberReset ? '' : customer.phone, attributes: [...updatedAttributes] };

    if (passwordReset) {
      handlePasswordReset();
    }

    dispatch(setSelectedCustomer(updatedCustomer));
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => onClose()}
      maxWidth="sm"
      PaperProps={{
        style: { borderRadius: 10 },
      }}
    >
      <DialogContent>
        <Grid container spacing={2} alignItems="center" justifyContent="center">
          <DialogTitle sx={{ fontSize: 30, m: 0, p: 2 }}>
            {modalContent.header}
            {onClose && (
              <IconButton
                aria-label="close"
                onClick={() => onClose()}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
          <Grid item xs={12}>
            <Typography variant="body1">{modalContent.body}</Typography>
          </Grid>
          <Grid item xs={12}>
            <DialogActions sx={{ justifyContent: 'center' }}>
              <Button
                sx={{
                  display: 'block',
                  marginRight: '10px',
                  backgroundColor: '#aaaaaa',
                  '&.MuiButtonBase-root:hover': {
                    bgcolor: '#aaaaaa',
                  },
                }}
                variant="contained"
                onClick={() => onClose(true)}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
              <LoadingButton variant="contained" onClick={() => resetAttributes()} data-testid="reset-button">
                {resetBtnLabel ?? 'Reset'}
              </LoadingButton>
            </DialogActions>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
